/**
 * The billing batch drivers (batch train; founder order 2026-08-31:
 * "we don't yet have batch processing, including batch compiling. make
 * this happen").
 *
 * THE STRUCTURAL LAW, verbatim from BatchPushBar: a batch is a LOOP over
 * the same per-item path a single act uses. No bulk endpoint, no shared
 * transaction. These are pushInvoicesSequentially's siblings (per-item
 * await, per-item try/catch, outcome buckets, onProgress), and they are
 * STRICTLY SEQUENTIAL by design, never parallel: issue's doc_number is
 * MAX+1 with a bounded retry (api/src/functions/invoicesIssue.ts), so a
 * parallel batch would manufacture its own conflicts. Compile itself
 * serializes per client (compileInvoice's advisory lock), so two
 * compiles of one client never both pass the overlap check.
 *
 * The transport sorting follows the ruled push-driver contract (QBO
 * sweep D4-4): a 4xx from Olive's own API is a provable pre-wire
 * refusal, so it is a NAMED per-item outcome and the loop continues. A
 * 5xx or a dead socket is delivery-unknown: the server may have finished
 * the write before the connection died. The push, approve and issue
 * drivers STOP there, report progress so far, and file the in-flight
 * item in NO bucket (pushInvoicesSequentially's `unconfirmed`
 * semantics). The compile driver goes on after a 5xx, filing that
 * client as unconfirmed, because a repeat of a compile that committed
 * answers "already invoiced"; a dead socket still stops it, since the
 * next call would meet the same connection.
 */

import { ApiError, invoicesApi, type CompileInvoiceBody, type UnbilledWorkRowWire } from './api';
import { contactSentence, type SupportContact } from './errorCopy';
import { fetchPrebilling, type PrebillingNote } from './prebilling';

/** Safety valve, sized generously above the go-live roster (Nov 1 at
 *  ~250 clients): anything past this bound means a runaway caller, and
 *  the report says so in a plain line rather than hammering the API. */
export const MAX_BATCH_CALLS = 400;

// ---------------------------------------------------------------------
// Month bounds
// ---------------------------------------------------------------------

/** Inclusive calendar bounds for a 'YYYY-MM' month key - the same
 *  period a hand compile for "the month" would type into the form. */
export function monthBounds(month: string): { period_start: string; period_end: string } {
  const [y, m] = month.split('-').map((part) => Number.parseInt(part, 10));
  const year = y ?? 0;
  const monthNum = m ?? 1;
  // Day 0 of the NEXT month is the last day of this one.
  const lastDay = new Date(year, monthNum, 0).getDate();
  const mm = String(monthNum).padStart(2, '0');
  return {
    period_start: `${year}-${mm}-01`,
    period_end: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
  };
}

// ---------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------

/** The same row identity the push batch carries: enough to NAME the
 *  invoice in the report, nothing more. */
export interface BatchActionTarget {
  id: string;
  doc_number: string | null;
  client_display_name: string;
}

export interface BatchActionOutcome {
  target: BatchActionTarget;
  /** The server's error class for refused rows; null when nothing was
   *  refused. Fact-shaped: the sentence lives in the rendering tier. */
  reason: string | null;
}

/** Why a run ended early. `after` names WHO the loop stopped after, in
 *  the operator's words (the display name), because "stopped after
 *  Fernmock, Isadora" is what tells them where to look. */
export interface BatchStop {
  after: string;
  /** 'network_failed' | 'server_error' | 'batch_call_limit' */
  reason: string;
}

export interface BatchActionReport {
  /** The act happened, in THIS run. */
  done: BatchActionOutcome[];
  /** 409 illegal_transition: the row moved on before the loop reached
   *  it. Benign by construction - approve/issue on a moved row is the
   *  repeat-safe case, not a failure to retry. */
  state_changed: BatchActionOutcome[];
  /** The server said NO to this row, pre-wire, with a class the report
   *  keeps. The only bucket allowed to say "was refused". */
  failed: BatchActionOutcome[];
  /** Set when the loop ended before the last target: a dead socket, a
   *  5xx, or the call bound. The in-flight item of a transport stop is
   *  in NO bucket above - Olive cannot know whether it landed. */
  stopped: BatchStop | null;
}

/** Progress after each settled item: (completed, total, display name of
 *  the item that just settled). */
export type BatchProgress = (completed: number, total: number, name: string) => void;

function errorCode(err: ApiError): string | null {
  if (err.body !== null && typeof err.body === 'object') {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === 'string' && code !== '') return code;
  }
  return null;
}

/** The transport sort (D4-4, applied to a stop-capable loop):
 *  'refused' = provable pre-wire no (4xx from Olive's own API);
 *  'unknown' = the write may have happened (5xx / dead socket). */
function classifyThrow(err: unknown): { kind: 'refused'; code: string } | { kind: 'unknown'; code: string } {
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    return { kind: 'refused', code: errorCode(err) ?? 'request_refused' };
  }
  if (err instanceof ApiError) {
    return { kind: 'unknown', code: 'server_error' };
  }
  return { kind: 'unknown', code: 'network_failed' };
}

// ---------------------------------------------------------------------
// Batch approve / batch issue
// ---------------------------------------------------------------------

interface BatchActionOpts {
  onProgress?: BatchProgress;
  /** Test seam, the push driver's idiom: the per-item call. */
  act?: (slug: string, id: string) => Promise<unknown>;
}

async function runActionSequentially(
  slug: string,
  targets: readonly BatchActionTarget[],
  act: (slug: string, id: string) => Promise<unknown>,
  onProgress: BatchProgress | undefined,
): Promise<BatchActionReport> {
  const report: BatchActionReport = { done: [], state_changed: [], failed: [], stopped: null };

  for (const [index, target] of targets.entries()) {
    if (index >= MAX_BATCH_CALLS) {
      report.stopped = {
        after: targets[index - 1]!.client_display_name,
        reason: 'batch_call_limit',
      };
      break;
    }
    try {
      await act(slug, target.id);
      report.done.push({ target, reason: null });
    } catch (err) {
      const sorted = classifyThrow(err);
      if (sorted.kind === 'unknown') {
        // Delivery-unknown: the act may have happened server-side. The
        // item goes in NO bucket, and the loop may not keep walking a
        // connection it cannot trust.
        report.stopped = { after: target.client_display_name, reason: sorted.code };
        break;
      }
      if (sorted.code === 'illegal_transition') {
        report.state_changed.push({ target, reason: sorted.code });
      } else {
        report.failed.push({ target, reason: sorted.code });
      }
    }
    onProgress?.(index + 1, targets.length, target.client_display_name);
  }

  return report;
}

/** draft -> approved, one POST per selected draft, in row order. */
export function approveInvoicesSequentially(
  slug: string,
  targets: readonly BatchActionTarget[],
  opts: BatchActionOpts = {},
): Promise<BatchActionReport> {
  const act = opts.act ?? ((s: string, id: string) => invoicesApi.approve(s, id));
  return runActionSequentially(slug, targets, act, opts.onProgress);
}

/** approved -> issued. MUST stay sequential: doc numbers are MAX+1
 *  under a unique constraint, and a parallel issue batch would spend
 *  the server's bounded retries fighting itself. */
export function issueInvoicesSequentially(
  slug: string,
  targets: readonly BatchActionTarget[],
  opts: BatchActionOpts = {},
): Promise<BatchActionReport> {
  const act = opts.act ?? ((s: string, id: string) => invoicesApi.issue(s, id));
  return runActionSequentially(slug, targets, act, opts.onProgress);
}

// ---------------------------------------------------------------------
// Compile the month
// ---------------------------------------------------------------------

export interface CompileCandidate {
  client_id: string;
  client_display_name: string;
}

/** A client the run will not compile because none of its month's notes
 *  is Ready: named before the run and in the report, never called. */
export interface NotReadyClient extends CompileCandidate {
  /** The notes waiting on Pre-billing. */
  not_ready_count: number;
}

export interface CompileOutcome {
  candidate: CompileCandidate;
  /** The server's class for skipped/failed rows; null on compiled. */
  reason: string | null;
}

export interface CompileMonthReport {
  /** A draft exists now that did not before. */
  compiled: CompileOutcome[];
  /** Waiting on reconcile: pre-Olive imported hours that were never
   *  signed off. SKIPPED LOUDLY - reported by name, never called and
   *  failed (the reconcile gate is CLI-only today; a 409 wall helps
   *  nobody). Holds both the pre-filtered rows and any
   *  reconcile_not_cleared the server answers despite the filter. */
  skipped_reconcile: CompileOutcome[];
  /** invoice_exists_for_period: the month is already covered. Can
   *  happen mid-loop (a hand compile raced the batch) and is benign. */
  already_invoiced: CompileOutcome[];
  /** no_billable_events: nothing to invoice. A benign skip. */
  no_billable_events: CompileOutcome[];
  /** Any other refusal, with its per-client error code kept. */
  failed: CompileOutcome[];
  /** A server error (5xx): the compile may have committed before the
   *  answer was lost, so the report claims neither way (reason
   *  server_error) and the run goes on. A repeat of a compile that
   *  committed answers "already invoiced", so a re-run is safe. */
  unconfirmed: CompileOutcome[];
  /** The clients nothing is ready for, never called. */
  not_ready: NotReadyClient[];
  stopped: BatchStop | null;
}

/**
 * The ONE derivation the count line and the driver both read, so the
 * number offered up front and the loop that runs cannot disagree.
 *
 * 'not_invoiced' is exactly "compile would work": the unbilled repo
 * computes it with the same overlap predicate compile enforces.
 * 'invoice_already_issued' is a candidate too: every invoice over the
 * month was sent, so compile makes a second invoice for the unbilled
 * charges. A row carrying 'reconcile_first' is set aside FIRST, whatever
 * else it carries - it can only 409, and the skip must be loud, not
 * attempted. A row carrying 'no_ready_notes' has nothing to compile: it
 * is named, never called. invoice_not_sent (an unsent invoice over the
 * month, whose remedy is Add unbilled charges on it) and rate_card raise
 * no candidate. A demo client's row is a candidate exactly as any other.
 */
export function splitCompileCandidates(rows: readonly UnbilledWorkRowWire[]): {
  candidates: CompileCandidate[];
  reconcileFirst: CompileCandidate[];
  notReady: NotReadyClient[];
} {
  const reconcileFirst: CompileCandidate[] = [];
  const notReady: NotReadyClient[] = [];
  // A couple on one invoice (0215): a partner's work goes on its payer's
  // invoice, so its row compiles the PAYER, once per household, named
  // payer first and then the partners with work, by name. A payer with no
  // row of its own is compiled when a partner's row is a candidate.
  const households = new Map<string, { payer: string; partners: Set<string> }>();
  for (const row of rows) {
    const candidate: CompileCandidate = {
      client_id: row.client_id,
      client_display_name: row.client_display_name,
    };
    if (row.reasons.includes('reconcile_first')) {
      reconcileFirst.push(candidate);
    } else if (row.reasons.includes('not_invoiced') || row.reasons.includes('invoice_already_issued')) {
      const payer = row.bills_with ?? null;
      const target = payer?.client_id ?? row.client_id;
      const entry = households.get(target) ?? {
        payer: payer?.display_name ?? row.client_display_name,
        partners: new Set<string>(),
      };
      if (payer === null) entry.payer = row.client_display_name;
      else entry.partners.add(row.client_display_name);
      households.set(target, entry);
    } else if (row.reasons.includes('no_ready_notes')) {
      notReady.push({ ...candidate, not_ready_count: row.not_ready_count });
    }
  }
  const candidates: CompileCandidate[] = [...households.entries()].map(([client_id, h]) => {
    const partners = [...h.partners].sort((a, b) => a.localeCompare(b));
    return {
      client_id,
      client_display_name: partners.length === 0 ? h.payer : [h.payer, ...partners].join(' and '),
    };
  });
  return { candidates, reconcileFirst, notReady };
}

interface CompileMonthOpts {
  onProgress?: BatchProgress;
  compile?: (slug: string, body: CompileInvoiceBody) => Promise<unknown>;
}

/**
 * Compile the picked month for every client the unbilled report says is
 * waiting on exactly that, one POST per client through the same
 * per-client compile route the form uses.
 */
export async function compileMonthSequentially(
  slug: string,
  month: string,
  rows: readonly UnbilledWorkRowWire[],
  opts: CompileMonthOpts = {},
): Promise<CompileMonthReport> {
  const compile = opts.compile ?? ((s: string, body: CompileInvoiceBody) => invoicesApi.compile(s, body));
  const { candidates, reconcileFirst, notReady } = splitCompileCandidates(rows);
  const bounds = monthBounds(month);

  const report: CompileMonthReport = {
    compiled: [],
    skipped_reconcile: reconcileFirst.map((candidate) => ({
      candidate,
      reason: 'reconcile_first',
    })),
    already_invoiced: [],
    no_billable_events: [],
    failed: [],
    unconfirmed: [],
    not_ready: notReady,
    stopped: null,
  };

  for (const [index, candidate] of candidates.entries()) {
    if (index >= MAX_BATCH_CALLS) {
      report.stopped = {
        after: candidates[index - 1]!.client_display_name,
        reason: 'batch_call_limit',
      };
      break;
    }
    try {
      await compile(slug, {
        client_id: candidate.client_id,
        period_start: bounds.period_start,
        period_end: bounds.period_end,
      });
      report.compiled.push({ candidate, reason: null });
    } catch (err) {
      const sorted = classifyThrow(err);
      if (sorted.kind === 'unknown' && sorted.code === 'server_error') {
        // The server answered: the compile may or may not have committed.
        // File it as not confirmed and go on; a dead connection stops.
        report.unconfirmed.push({ candidate, reason: sorted.code });
        opts.onProgress?.(index + 1, candidates.length, candidate.client_display_name);
        continue;
      }
      if (sorted.kind === 'unknown') {
        report.stopped = { after: candidate.client_display_name, reason: sorted.code };
        break;
      }
      if (sorted.code === 'invoice_exists_for_period') {
        report.already_invoiced.push({ candidate, reason: sorted.code });
      } else if (sorted.code === 'no_billable_events') {
        report.no_billable_events.push({ candidate, reason: sorted.code });
      } else if (sorted.code === 'reconcile_not_cleared') {
        // The pre-filter should have caught it; when the server says it
        // anyway, it is STILL the reconcile story, never a failure.
        report.skipped_reconcile.push({ candidate, reason: sorted.code });
      } else {
        report.failed.push({ candidate, reason: sorted.code });
      }
    }
    opts.onProgress?.(index + 1, candidates.length, candidate.client_display_name);
  }

  return report;
}

// ---------------------------------------------------------------------
// Export the month: the billing CSV for every client
// ---------------------------------------------------------------------

/** A roster row, reduced to what the loop needs: the id to read and the
 *  name the file prints and the report says. */
export interface ExportClient {
  id: string;
  display_name: string;
}

/** One client's month as READ: the name the CSV prints and the rows the
 *  builder filters to Ready. A client with no rows is still an entry,
 *  so the report can say how many clients were read, not only how many
 *  had lines. */
export interface ExportEntry {
  clientId: string;
  clientName: string;
  rows: PrebillingNote[];
}

export interface ExportOutcome {
  client: ExportClient;
  /** The server's error class for the refused read. */
  reason: string | null;
}

export interface ExportMonthReport {
  /** Every client whose month was read, in the order given. */
  entries: ExportEntry[];
  /** The server said NO to reading this client (a 4xx), class kept. The
   *  loop went on; the file is short by these, by name. */
  refused: ExportOutcome[];
  /** Set when the loop ended before the last client: a dead socket, a
   *  5xx, or the call bound. The client the loop stopped on is in NO
   *  bucket above. */
  stopped: BatchStop | null;
}

interface ExportMonthOpts {
  onProgress?: BatchProgress;
  /** Test seam: the per-client read. */
  read?: (slug: string, clientId: string, month: string) => Promise<{ rows: PrebillingNote[] }>;
}

/**
 * Read the picked month for every client given, one GET per client
 * through the same per-client prebilling route the Pre-billing tab
 * uses (one phi_access_log row per client, exactly as the tab would
 * write), strictly in order.
 *
 * The transport sort is the write drivers' sort for a different reason:
 * a 4xx is a provable per-client refusal, named and walked past, so one
 * client the server will not show does not cost the other two hundred
 * their lines. A 5xx or a dead socket STOPS the loop, because a billing
 * file that quietly ends where the connection did looks complete and
 * is not; the caller downloads nothing on a stop.
 */
export async function exportMonthSequentially(
  slug: string,
  month: string,
  clients: readonly ExportClient[],
  opts: ExportMonthOpts = {},
): Promise<ExportMonthReport> {
  const read =
    opts.read ?? ((s: string, clientId: string, m: string) => fetchPrebilling(s, clientId, m));
  const report: ExportMonthReport = { entries: [], refused: [], stopped: null };

  for (const [index, client] of clients.entries()) {
    if (index >= MAX_BATCH_CALLS) {
      report.stopped = { after: clients[index - 1]!.display_name, reason: 'batch_call_limit' };
      break;
    }
    try {
      const { rows } = await read(slug, client.id, month);
      report.entries.push({ clientId: client.id, clientName: client.display_name, rows });
    } catch (err) {
      const sorted = classifyThrow(err);
      if (sorted.kind === 'unknown') {
        report.stopped = { after: client.display_name, reason: sorted.code };
        break;
      }
      report.refused.push({ client, reason: sorted.code });
    }
    opts.onProgress?.(index + 1, clients.length, client.display_name);
  }

  return report;
}

// ---------------------------------------------------------------------
// Tokens into sentences (the qboApi REASON_SENTENCES idiom: one place,
// so a class nobody wrote a sentence for still renders as something
// true rather than as blank space or a wire token; a code is never
// printed).
// ---------------------------------------------------------------------

const ACTION_REASON_SENTENCES: Readonly<Record<string, string>> = {
  illegal_transition: 'Someone changed this invoice partway through, so it was left alone. Reload the page to see where it stands.',
  no_billable_events: 'Nothing billable is left on it. Open it to see where its charges went.',
  reconcile_not_cleared:
    "This invoice has imported hours that were never signed off, so it can't move yet. Ask {contact_billing} to sign them off.",
  approval_invalidated: "Its work changed after it was approved, so it wasn't issued. Press Reopen, then approve it again.",
  invoice_not_found: 'This invoice is gone, so nothing was done. Reload the page to see the current list.',
  request_refused:
    "This one didn't go through, and nothing changed. Reload the page and try again, and if it happens again, tell {contact_system}.",
  insufficient_capability: "This isn't part of your access, so nothing was done. Ask {contact_billing} to do it.",
};

export function batchActionReasonSentence(reason: string | null, contact: SupportContact): string {
  const own = reason !== null ? ACTION_REASON_SENTENCES[reason] : undefined;
  return contactSentence(
    own ??
      "This one didn't go through, and nothing changed. Reload the page and try again, and if it happens again, tell {contact_system}.",
    contact,
  );
}

const COMPILE_REASON_SENTENCES: Readonly<Record<string, string>> = {
  reconcile_first:
    'Skipped, because its hours imported from the old system were never signed off. Ask {contact_billing} to sign them off, then compile again.',
  reconcile_not_cleared:
    'No invoice was made, because its hours imported from the old system were never signed off. Ask {contact_billing} to sign them off, then compile again.',
  invoice_exists_for_period:
    "No invoice was made, because this client's invoice for this month isn't sent yet. Open that invoice and choose Add unbilled charges.",
  no_billable_events: 'Nothing to bill this month, so no invoice was made. If you expected some, check that the notes are marked Ready.',
  client_not_found: "This client can't be found, so no invoice was made. Reload the page, then compile again.",
  request_refused:
    "This one didn't go through, so no invoice was made. Reload the page and compile again, and if it happens again, tell {contact_system}.",
  insufficient_capability: "Making invoices isn't part of your access, so none was made. Ask {contact_billing} to do it.",
  server_error: "It isn't clear whether this invoice was made. Open the client's invoices to check before you compile again.",
};

export function compileReasonSentence(reason: string | null, contact: SupportContact): string {
  const own = reason !== null ? COMPILE_REASON_SENTENCES[reason] : undefined;
  return contactSentence(
    own ?? "No invoice was made for this client. Open their Pre-billing tab to see what's holding it, then compile again.",
    contact,
  );
}

const EXPORT_REFUSED_SENTENCES: Readonly<Record<string, string>> = {
  client_not_found: "This client can't be found, so they're not in the file. Reload the page and download again.",
  request_refused:
    "This client's billing couldn't be read, so they're not in the file. Download again, and if it happens again, tell {contact_system}.",
  insufficient_capability:
    "Your access doesn't include this client's billing, so they're not in the file. Ask {contact_billing} to run the download.",
};

export function exportRefusedSentence(reason: string | null, contact: SupportContact): string {
  const own = reason !== null ? EXPORT_REFUSED_SENTENCES[reason] : undefined;
  return contactSentence(
    own ??
      "This client's billing couldn't be read, so they're not in the file. Download again, and if it happens again, tell {contact_system}.",
    contact,
  );
}

/** The export's stop line. Reads have no in-flight write to worry
 *  about; what the sentence must carry is that NO file was made, so a
 *  short one can never pass for the month. */
export function exportStopSentence(stop: BatchStop): string {
  if (stop.reason === 'batch_call_limit') {
    return `This stopped after ${stop.after} because the download was too big, so no file was made. Pick a shorter date range, then download again.`;
  }
  return `This stopped at ${stop.after}, so no file was made rather than one with clients missing. Press Download billing CSV again.`;
}

/** The run a stop line is about: a compile goes client by client, a batch
 *  approve or issue invoice by invoice. */
export type BatchStopKind = 'compile' | 'approve' | 'issue';

const STOP_WORDS: Readonly<Record<BatchStopKind, { button: string; subject: string; done: string }>> = {
  compile: { button: 'Compile', subject: "that client's invoice", done: 'made' },
  approve: { button: 'Approve', subject: 'that invoice', done: 'approved' },
  issue: { button: 'Issue', subject: 'that invoice', done: 'issued' },
};

/** The stop line's sentence, naming who it stopped at and the button
 *  that carries it on (`after` is the last one done for the bound stop,
 *  the one in flight for a transport stop). A transport stop must not claim the
 *  in-flight item either way; the bound stop may, because its last item
 *  finished before the loop stopped. */
export function batchStopSentence(stop: BatchStop, kind: BatchStopKind): string {
  const words = STOP_WORDS[kind];
  if (stop.reason === 'batch_call_limit') {
    return `This stopped after ${stop.after} because the list was long, so the rest aren't done yet. Press ${words.button} again to carry on with the rest.`;
  }
  return `This stopped at ${stop.after}, and it isn't clear whether ${words.subject} was ${words.done}. Reload the page to see where each one stands before you go on.`;
}

